'use client';

import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  ANALYTICS_CONSENT_COOKIE_NAME,
  ANALYTICS_CONSENT_STORAGE_KEY,
  buildAnalyticsConsentCookie,
  parseAnalyticsConsent,
  type AnalyticsConsentDecision,
} from '@/lib/analyticsConsent';

type YandexMetrikaFn = ((...args: unknown[]) => void) & {
  a?: unknown[][];
  l?: number;
};

declare global {
  interface Window {
    ym?: YandexMetrikaFn;
    __sShotYandexMetrikaLoaded?: boolean;
    __sShotYandexMetrikaInitialized?: boolean;
  }
}

const YANDEX_METRIKA_COUNTER_ID = 1805029;
const YANDEX_METRIKA_SCRIPT_URL = 'https://mc.yandex.ru/metrika/tag.js';

function readConsentFromCookies(cookieValue: string): AnalyticsConsentDecision | null {
  const consentEntry = cookieValue
    .split(';')
    .map((entry) => entry.trim())
    .find((entry) => entry.startsWith(`${ANALYTICS_CONSENT_COOKIE_NAME}=`));

  if (!consentEntry) {
    return null;
  }

  const [, rawValue = ''] = consentEntry.split('=');
  return parseAnalyticsConsent(rawValue);
}

function readStoredAnalyticsConsent(): AnalyticsConsentDecision | null {
  try {
    const stored = parseAnalyticsConsent(window.localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY));
    if (stored) {
      return stored;
    }
  } catch {
    // Ignore storage access failures and fall back to cookies.
  }

  if (typeof document === 'undefined') {
    return null;
  }

  return readConsentFromCookies(document.cookie);
}

function persistAnalyticsConsent(decision: AnalyticsConsentDecision) {
  try {
    window.localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, decision);
  } catch {
    // Ignore storage access failures and still persist a cookie.
  }

  document.cookie = buildAnalyticsConsentCookie(decision);
}

function ensureYandexMetrikaLoaded() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const existingQueue = window.ym;
  if (!existingQueue) {
    const queue = ((...args: unknown[]) => {
      queue.a = queue.a || [];
      queue.a.push(args);
    }) as YandexMetrikaFn;
    queue.l = Date.now();
    window.ym = queue;
  }

  const hasExistingScript = Array.from(document.scripts).some(
    (script) => script.src === YANDEX_METRIKA_SCRIPT_URL,
  );

  if (hasExistingScript) {
    return;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = YANDEX_METRIKA_SCRIPT_URL;
  script.addEventListener('load', () => {
    window.__sShotYandexMetrikaLoaded = true;
  });
  document.head.appendChild(script);
}

function subscribeToLocationChanges(onChange: () => void): () => void {
  const { history } = window;
  const originalPushState = history.pushState.bind(history);
  const originalReplaceState = history.replaceState.bind(history);

  const notify = () => {
    onChange();
  };

  history.pushState = ((...args) => {
    originalPushState(...args);
    notify();
  }) as History['pushState'];

  history.replaceState = ((...args) => {
    originalReplaceState(...args);
    notify();
  }) as History['replaceState'];

  window.addEventListener('popstate', notify);
  window.addEventListener('hashchange', notify);

  return () => {
    history.pushState = originalPushState;
    history.replaceState = originalReplaceState;
    window.removeEventListener('popstate', notify);
    window.removeEventListener('hashchange', notify);
  };
}

export function AnalyticsConsentManager({ enabled }: { enabled: boolean }) {
  const trackedUrlRef = useRef<string | null>(null);
  const [consent, setConsent] = useState<AnalyticsConsentDecision | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setConsent(null);
      setShowPrompt(false);
      setInitialized(true);
      return;
    }

    const storedConsent = readStoredAnalyticsConsent();
    setConsent(storedConsent);
    setInitialized(true);

    if (storedConsent) {
      setShowPrompt(false);
      return;
    }

    const timeout = window.setTimeout(() => {
      setShowPrompt(true);
    }, 1200);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !initialized || !consent) {
      return;
    }

    persistAnalyticsConsent(consent);
    setShowPrompt(false);
  }, [enabled, initialized, consent]);

  useEffect(() => {
    if (!enabled || consent !== 'accepted') {
      return;
    }

    ensureYandexMetrikaLoaded();

    const currentUrl = window.location.href;
    if (!window.__sShotYandexMetrikaInitialized && window.ym) {
      window.ym(YANDEX_METRIKA_COUNTER_ID, 'init', {
        accurateTrackBounce: true,
        trackLinks: true,
      });
      window.__sShotYandexMetrikaInitialized = true;
      trackedUrlRef.current = currentUrl;
    }

    const trackCurrentUrl = () => {
      const nextUrl = window.location.href;
      if (!window.__sShotYandexMetrikaInitialized || !window.ym || trackedUrlRef.current === nextUrl) {
        return;
      }

      window.ym(YANDEX_METRIKA_COUNTER_ID, 'hit', nextUrl, {
        title: document.title,
        referer: document.referrer,
      });
      trackedUrlRef.current = nextUrl;
    };

    trackCurrentUrl();
    return subscribeToLocationChanges(trackCurrentUrl);
  }, [enabled, consent]);

  if (!enabled || !initialized || consent || !showPrompt) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[90] p-4 sm:p-6">
      <div className="pointer-events-auto mx-auto flex max-w-3xl flex-col gap-4 rounded-[1.75rem] border border-[color:var(--site-border-soft)] bg-[color:rgba(255,255,255,0.96)] p-5 shadow-[0_24px_80px_rgba(23,38,59,0.16)] backdrop-blur sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--site-ink-subtle)]">
            Аналитика
          </p>
          <p className="text-sm leading-6 text-[var(--site-ink)]">
            Мы загрузим Yandex Metrika только после вашего согласия, чтобы измерять эффективность публичных страниц.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:min-w-[17rem]">
          <Button type="button" className="w-full" onClick={() => setConsent('accepted')}>
            Разрешить аналитику
          </Button>
          <Button type="button" variant="outline" className="w-full" onClick={() => setConsent('declined')}>
            Только обязательные cookie
          </Button>
        </div>
      </div>
    </div>
  );
}
